import { SiteDashboardWidgetData, SiteDashboardWidgetError } from "~/api/Api";

import { DecodedWidgetConfigSchema } from "../types/DecodedWidgetConfig.type";
import {
  GoogleAnalyticsPropertyRealtimeReportSchema,
  GoogleAnalyticsPropertyReportSchema,
  GoogleSearchConsoleReportSchema,
  RenderableWidgetOrError,
  WidgetConfig,
} from "../types/RenderableWidget.type";

export const toRenderableWidget = (
  config: WidgetConfig,
  data: SiteDashboardWidgetData | SiteDashboardWidgetError,
): RenderableWidgetOrError => {
  try {
    const decodedConfig = DecodedWidgetConfigSchema.parse(config);

    if (isWidgetError(data)) {
      return {
        id: config.id,
        config: decodedConfig,
        type: decodedConfig.method,
        error: new Error(widgetErrorCodeToMessage(data.data.error)),
      };
    }

    switch (decodedConfig.method) {
      case "property_report":
        return {
          id: config.id,
          config: decodedConfig,
          type: decodedConfig.method,
          data: GoogleAnalyticsPropertyReportSchema.parse(data.data),
        };
      case "property_realtime_report":
        return {
          id: config.id,
          config: decodedConfig,
          type: decodedConfig.method,
          data: GoogleAnalyticsPropertyRealtimeReportSchema.parse(data.data),
        };
      case "search_analytics":
        return {
          id: config.id,
          config: decodedConfig,
          type: decodedConfig.method,
          data: GoogleSearchConsoleReportSchema.parse(data.data),
        };
    }
  } catch (err) {
    // Surface schema mismatches as a per-widget error instead of crashing
    // the entire dashboard via the ErrorBoundary.
    const decodedConfig = DecodedWidgetConfigSchema.safeParse(config);
    const fallbackConfig = decodedConfig.success ? decodedConfig.data : null;

    return {
      id: config.id,
      config: fallbackConfig as RenderableWidgetOrError["config"],
      type: (fallbackConfig?.method ??
        "property_report") as RenderableWidgetOrError["type"],
      error: err instanceof Error ? err : new Error(String(err)),
    };
  }
};

const isWidgetError = (
  widget: SiteDashboardWidgetData | SiteDashboardWidgetError,
): widget is SiteDashboardWidgetError => {
  return "error" in widget.data && typeof widget.data.error === "string";
};

const widgetErrorCodeToMessage = (errorCode: string) => {
  switch (errorCode) {
    case "integration_not_found":
      return "not configured, please check your integration settings.";
    case "integration_incomplete":
      return "integration incomplete, please check your integration settings.";
    case "integration_unauthorized":
      return "unauthorized, please check your Google account permissions or reconnect your Google account.";
    case "query_invalid":
      return "invalid query.";
    case "query_error":
      return "please check your integration settings.";
    default:
      return "unknown error.";
  }
};
